import { EditorSection } from '@/components/plot/editor/EditorSection';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEffect, useState, useMemo } from 'react';
import { LegendControls } from '../LegendControls';

export function FigureSharedTab() {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const setAxesSharing = useFigureCompositionStore((state) => state.setAxesSharing);
  const [standardizationExpanded, setStandardizationExpanded] = useState<string | undefined>(undefined);

  if (!currentComposition) return null;

  const axesSharing = currentComposition.axesSharing ?? {
    shareXAcrossColumns: false,
    shareXAcrossAll: false,
    shareYAcrossRows: false,
    warnOnScaleMismatch: false,
    standardizeXTitles: false,
    standardizeYTitles: false,
    standardizeXScales: false,
    standardizeYScales: false,
  };

  // Fix invalid state: if standardization is checked but axis linking is not, uncheck standardization
  // This handles existing invalid data. The store logic prevents new invalid states from being created.
  useEffect(() => {
    const hasXAxisLinking = axesSharing.shareXAcrossColumns || axesSharing.shareXAcrossAll;
    const hasYAxisLinking = axesSharing.shareYAcrossRows;
    
    const needsFix = 
      (!hasXAxisLinking && (axesSharing.standardizeXTitles || axesSharing.standardizeXScales)) ||
      (!hasYAxisLinking && (axesSharing.standardizeYTitles || axesSharing.standardizeYScales));
    
    if (needsFix) {
      const updates: Partial<typeof axesSharing> = {};
      if (!hasXAxisLinking) {
        if (axesSharing.standardizeXTitles) updates.standardizeXTitles = false;
        if (axesSharing.standardizeXScales) updates.standardizeXScales = false;
      }
      if (!hasYAxisLinking) {
        if (axesSharing.standardizeYTitles) updates.standardizeYTitles = false;
        if (axesSharing.standardizeYScales) updates.standardizeYScales = false;
      }
      setAxesSharing(updates);
    }
  }, [
    axesSharing.shareXAcrossColumns,
    axesSharing.shareXAcrossAll,
    axesSharing.shareYAcrossRows,
    axesSharing.standardizeXTitles,
    axesSharing.standardizeXScales,
    axesSharing.standardizeYTitles,
    axesSharing.standardizeYScales,
    setAxesSharing,
  ]);

  // Expand standardization section when link x or link y is selected
  useEffect(() => {
    const hasXAxisLinking = axesSharing.shareXAcrossColumns || axesSharing.shareXAcrossAll;
    const hasYAxisLinking = axesSharing.shareYAcrossRows;
    
    if (hasXAxisLinking || hasYAxisLinking) {
      setStandardizationExpanded('axes-standardization');
    }
  }, [axesSharing.shareXAcrossColumns, axesSharing.shareXAcrossAll, axesSharing.shareYAcrossRows]);

  // Compute shared label style values
  const labelStyleValues = useMemo(() => {
    const panels = currentComposition.panels.filter(p => p.label);
    if (panels.length === 0) {
      return {
        position: 'top-left' as const,
        fontSize: 10,
        fontWeight: false,
        fontStyle: false,
      };
    }
    
    const positions = panels.map(p => p.labelStyle?.position ?? 'top-left');
    const sizes = panels.map(p => p.labelStyle?.fontSize ?? 10);
    const weights = panels.map(p => (p.labelStyle?.fontWeight ?? 'bold') === 'bold');
    const styles = panels.map(p => (p.labelStyle?.fontStyle ?? 'normal') === 'italic');
    
    return {
      position: positions.every(p => p === positions[0]) ? positions[0] : undefined,
      fontSize: sizes.every(s => s === sizes[0]) ? sizes[0] : undefined,
      fontWeight: weights.every(w => w === weights[0]) ? weights[0] : false,
      fontStyle: styles.every(s => s === styles[0]) ? styles[0] : false,
    };
  }, [currentComposition.panels]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <h3 className="text-xs font-semibold text-foreground">Shared elements</h3>
        <p className="text-[11px] text-muted-foreground">
          Configure axes, titles, and legends shared across panels.
        </p>
      </div>

      {/* Shared Axes Section */}
      <EditorSection
        id="axes-linking"
        title="Shared axes"
        description="Link scales and titles across panels for fair comparisons."
      >
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="share-x-columns"
              checked={axesSharing.shareXAcrossColumns}
              onCheckedChange={(checked) =>
                setAxesSharing({ shareXAcrossColumns: checked === true })
              }
            />
            <Label htmlFor="share-x-columns" className="text-sm cursor-pointer">
              Link X-axis within each column
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="share-x-all"
              checked={axesSharing.shareXAcrossAll}
              onCheckedChange={(checked) =>
                setAxesSharing({ shareXAcrossAll: checked === true })
              }
            />
            <Label htmlFor="share-x-all" className="text-sm cursor-pointer">
              Link X-axis across all panels
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="share-y-rows"
              checked={axesSharing.shareYAcrossRows}
              onCheckedChange={(checked) =>
                setAxesSharing({ shareYAcrossRows: checked === true })
              }
            />
            <Label htmlFor="share-y-rows" className="text-sm cursor-pointer">
              Link Y-axis within each row
            </Label>
          </div>
        </div>
      </EditorSection>

      <EditorSection
        id="axes-standardization"
        title="Axis standardization"
        description="Lock scales across linked axes. Locked scales prevent per-panel overrides."
        collapsible
        defaultCollapsed
        value={standardizationExpanded}
        onValueChange={setStandardizationExpanded}
      >
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="standardize-x-scales"
              checked={axesSharing.standardizeXScales ?? false}
              disabled={!axesSharing.shareXAcrossColumns && !axesSharing.shareXAcrossAll}
              onCheckedChange={(checked) =>
                setAxesSharing({ standardizeXScales: checked === true })
              }
            />
            <Label htmlFor="standardize-x-scales" className={`text-sm cursor-pointer ${(!axesSharing.shareXAcrossColumns && !axesSharing.shareXAcrossAll) ? 'text-muted-foreground' : ''}`}>
              Lock X-axis scales
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="standardize-y-scales"
              checked={axesSharing.standardizeYScales ?? false}
              disabled={!axesSharing.shareYAcrossRows}
              onCheckedChange={(checked) =>
                setAxesSharing({ standardizeYScales: checked === true })
              }
            />
            <Label htmlFor="standardize-y-scales" className={`text-sm cursor-pointer ${!axesSharing.shareYAcrossRows ? 'text-muted-foreground' : ''}`}>
              Lock Y-axis scales
            </Label>
          </div>
        </div>
      </EditorSection>

      {/* Panel Titles Section */}
      <EditorSection
        id="panel-titles"
        title="Titles and annotation"
        description="Control title and subtitle display for all panels."
      >
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="remove-all-titles"
              checked={
                currentComposition.panels.filter(p => p.plotId).length > 0 &&
                currentComposition.panels.filter(p => p.plotId).every(p => p.layoutOverrides?.hideTitle === true)
              }
              onCheckedChange={(checked) => {
                const updatePanelOverrides = useFigureCompositionStore.getState().updatePanelOverrides;
                currentComposition.panels.forEach((panel) => {
                  if (panel.plotId) {
                    updatePanelOverrides(panel.id, { hideTitle: checked === true });
                  }
                });
              }}
            />
            <Label htmlFor="remove-all-titles" className="text-sm cursor-pointer">
              Hide titles in all panels
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="remove-all-subtitles"
              checked={
                currentComposition.panels.filter(p => p.plotId).length > 0 &&
                currentComposition.panels.filter(p => p.plotId).every(p => p.layoutOverrides?.hideSubtitle === true)
              }
              onCheckedChange={(checked) => {
                const updatePanelOverrides = useFigureCompositionStore.getState().updatePanelOverrides;
                currentComposition.panels.forEach((panel) => {
                  if (panel.plotId) {
                    updatePanelOverrides(panel.id, { hideSubtitle: checked === true });
                  }
                });
              }}
            />
            <Label htmlFor="remove-all-subtitles" className="text-sm cursor-pointer">
              Hide subtitles in all panels
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="remove-all-annotations"
              checked={
                currentComposition.panels.filter(p => p.plotId).length > 0 &&
                currentComposition.panels.filter(p => p.plotId).every(p => p.layoutOverrides?.hideAnnotations === true)
              }
              onCheckedChange={(checked) => {
                const updatePanelOverrides = useFigureCompositionStore.getState().updatePanelOverrides;
                currentComposition.panels.forEach((panel) => {
                  if (panel.plotId) {
                    updatePanelOverrides(panel.id, { hideAnnotations: checked === true });
                  }
                });
              }}
            />
            <Label htmlFor="remove-all-annotations" className="text-sm cursor-pointer">
              Hide annotations in all panels
            </Label>
          </div>
        </div>
      </EditorSection>

      {/* Subpanel Labels Section */}
      <EditorSection
        id="subpanel-labels"
        title="Subpanel labels"
        description="Control typography of panel labels (A, B, C, etc.) across all panels."
      >
        <div className="space-y-3">
          {/* Position */}
          <div className="space-y-1">
            <Label className="text-sm text-muted-foreground">Position</Label>
            <Select
              value={labelStyleValues.position ?? 'top-left'}
              onValueChange={(value) => {
                const updatePanel = useFigureCompositionStore.getState().updatePanel;
                currentComposition.panels.forEach((panel) => {
                  if (panel.label) {
                    updatePanel(panel.id, {
                      labelStyle: {
                        ...panel.labelStyle,
                        position: value as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
                      },
                    });
                  }
                });
              }}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="top-left">Top-left</SelectItem>
                <SelectItem value="top-right">Top-right</SelectItem>
                <SelectItem value="bottom-left">Bottom-left</SelectItem>
                <SelectItem value="bottom-right">Bottom-right</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Font Weight and Style Checkboxes */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="subpanel-label-bold"
                checked={labelStyleValues.fontWeight}
                onCheckedChange={(checked) => {
                  const updatePanel = useFigureCompositionStore.getState().updatePanel;
                  currentComposition.panels.forEach((panel) => {
                    if (panel.label) {
                      updatePanel(panel.id, {
                        labelStyle: {
                          ...panel.labelStyle,
                          fontWeight: checked === true ? 'bold' : 'normal',
                        },
                      });
                    }
                  });
                }}
              />
              <Label htmlFor="subpanel-label-bold" className="text-sm cursor-pointer">
                Bold
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="subpanel-label-italic"
                checked={labelStyleValues.fontStyle}
                onCheckedChange={(checked) => {
                  const updatePanel = useFigureCompositionStore.getState().updatePanel;
                  currentComposition.panels.forEach((panel) => {
                    if (panel.label) {
                      updatePanel(panel.id, {
                        labelStyle: {
                          ...panel.labelStyle,
                          fontStyle: checked === true ? 'italic' : 'normal',
                        },
                      });
                    }
                  });
                }}
              />
              <Label htmlFor="subpanel-label-italic" className="text-sm cursor-pointer">
                Italic
              </Label>
            </div>
          </div>

          {/* Font Size */}
          <div className="space-y-1">
            <Label className="text-sm text-muted-foreground">Font size (pt)</Label>
            <Input
              type="number"
              min={6}
              max={24}
              step={1}
              className="h-9 w-full text-sm"
              value={labelStyleValues.fontSize !== undefined ? String(labelStyleValues.fontSize) : ''}
              placeholder="10"
              onChange={(e) => {
                const fontSize = parseInt(e.target.value, 10);
                if (!isNaN(fontSize) && fontSize > 0) {
                  const updatePanel = useFigureCompositionStore.getState().updatePanel;
                  currentComposition.panels.forEach((panel) => {
                    if (panel.label) {
                      updatePanel(panel.id, {
                        labelStyle: {
                          ...panel.labelStyle,
                          fontSize,
                        },
                      });
                    }
                  });
                }
              }}
            />
          </div>
        </div>
      </EditorSection>

      {/* Legends Section */}
      <EditorSection
        id="legend-mode"
        title="Legends"
        description="Control how legends are shared across panels."
      >
        <LegendControls />
      </EditorSection>
    </div>
  );
}

